import { ArrowCounterClockwise, File, Info } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Button, buttonStyles, Modal, Spinner } from '@skupreme/shared/ui';
import { DateFormat, DateUtility } from '@utility/date';
import { AppTable, AppTableCell, AppTableRow } from '@components/AppTable';
import { orderFeedListHook } from '@hooks/order';
import { CommonUtility } from '@utility/common';
import { DarkPopup } from '@elements/Popup';
import { TablePagination } from '@components/TablePagination';
import { FileUploadDragAndDrop, type PickedFile } from '@components/FileUpload';
import OrdersService from '@utility/services/order';
import { ToastMessage } from '@utility/toast';
import UploadService from '@utility/services/upload';
import type { ITableColumn } from 'types/common/table';

const columns: ITableColumn[] = [
  {
    text: 'Uploaded',
    key: 'created',
    width: 5,
    textAlign: 'left',
  },
  {
    text: 'Status',
    key: 'status',
    width: 3,
    textAlign: 'left',
  },
  {
    text: 'File',
    key: 'file',
    textAlign: 'left',
    width: 3,
  },
];

type ImportOrderPopupProps = {
  regionId?: string | null;
  open: boolean;
  onClose: () => void;
};

export function ImportOrderPopup({
  regionId,
  open,
  onClose,
}: ImportOrderPopupProps) {
  const [templateLoading, setTemplateLoading] = useState(false);
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [selectedRegionId, setSelectedRegionId] = useState(
    '6012c24092485d8b30c764e7',
  );

  useEffect(() => {
    if (!open) {
      setFiles([]);
    }
  }, [open]);

  const {
    data: orderFeedList,
    refresh,
    loading: orderLoading,
    pageChanged,
    filter,
    filterChanged,
    totalData,
    totalPages,
  } = orderFeedListHook();
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (regionId !== undefined && regionId !== null) {
      setSelectedRegionId(regionId);
    }
  }, [regionId]);

  const downloadEmptyTemplate = async () => {
    try {
      setTemplateLoading(true);
      const temp = {};
      const res = await OrdersService.downloadFeedTemplate(temp);
      CommonUtility.downloadCSV(res, 'order-feed-template');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setTemplateLoading(false);
    }
  };

  const uploadCSV = async () => {
    try {
      if (!CommonUtility.isValidArray(files)) {
        ToastMessage.error('Please select a file');
        return;
      }
      setUploading(true);
      const feedType = 'orders-import';
      const temp = {
        regionId: selectedRegionId,
        feedType,
      };
      const { uploadUrl } = await OrdersService.getOrderFeedUpload(
        feedType,
        temp,
      );
      UploadService.media(uploadUrl, files[0]?.file, files[0]?.file.type);

      ToastMessage.success('File uploaded successfully');
      refresh();
      onClose();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && onClose()}
      title={
        <span className="sk:flex sk:items-center sk:gap-2">
          Order Feed
          <Button
            variant="ghost"
            size="sm"
            shape="circle"
            aria-label="Refresh feeds"
            onPress={refresh}
            isDisabled={orderLoading}
          >
            <ArrowCounterClockwise size={14} />
          </Button>
        </span>
      }
      size="lg"
      showCloseButton
      footer={
        <>
          <Button variant="pill" onPress={onClose}>
            Close
          </Button>
          <Button variant="primary" onPress={uploadCSV} isDisabled={uploading}>
            {uploading && <Spinner size="sm" />}
            Upload
          </Button>
        </>
      }
    >
      <div className="sk:mb-3 sk:flex sk:flex-wrap sk:items-center sk:justify-between sk:gap-2">
        <p className="sk:m-0">
          Select a order feed or download a template from here
        </p>
        <Button
          variant="pill"
          onPress={downloadEmptyTemplate}
          isDisabled={templateLoading}
        >
          {templateLoading && <Spinner size="sm" />}
          Download Basic Template
        </Button>
      </div>
      <FileUploadDragAndDrop
        accept=".csv"
        files={files}
        setFiles={setFiles}
        maxFiles={1}
      />
      <div className="sk:mt-4">
        <span className="sk:font-semibold">Recent Feeds</span>
        <div className="sk:mb-2.5 sk:max-h-[45vh] sk:overflow-y-auto">
          <AppTable
            data={orderFeedList}
            loading={orderLoading}
            columns={columns}
            // @ts-expect-error - TS2741: Property 'item' is missing in type '{}' but required in type '{ item: any; }'.
            RowComponent={<FeedRow />}
            noShadow
            wrapperClass="sk:m-0!"
          />
        </div>
        <TablePagination
          noPadding
          colSpan={columns.length}
          currentPage={filter.page}
          totalPages={totalPages}
          pageChanged={pageChanged}
          pageSizeChanged={size => filterChanged('limit', size)}
          pageSize={filter.limit}
          totalData={totalData}
        />
      </div>
    </Modal>
  );
}

function MessageDetails({ results }) {
  const box = 'sk:flex sk:w-100 sk:max-w-full sk:flex-col sk:gap-0.5';

  if (CommonUtility.isValidArray(results?.errors)) {
    return (
      <div className={box}>
        {results.errors.map((error, index) => (
          <div key={`${index}-${error.message}`}>
            {index + 1}. {error.message}
          </div>
        ))}
      </div>
    );
  }

  if (CommonUtility.isValidHTML(results.messages.success)) {
    return (
      <div className={box}>
        <div dangerouslySetInnerHTML={{ __html: results.messages.success }} />
      </div>
    );
  }

  return (
    <div className={box}>
      <div>{results.messages.success}</div>
    </div>
  );
}

function FeedRow({ item }) {
  return (
    <AppTableRow>
      <AppTableCell>
        {DateUtility.formatDate(item.createdAt, DateFormat.dateTime)}
      </AppTableCell>
      <AppTableCell>
        <div className="sk:flex sk:items-center sk:gap-2">
          {CommonUtility.removeUnderscoreAndCapitalize(item.status)}
          <DarkPopup hoverable trigger={<Info className="sk:cursor-pointer" />}>
            <MessageDetails results={item.results} />
          </DarkPopup>
        </div>
      </AppTableCell>
      <AppTableCell>
        <a
          target="_blank"
          rel="noreferrer"
          download
          href={item.url}
          className={buttonStyles({ className: 'sk:w-fit sk:no-underline' })}
        >
          <File size={16} className="sk:shrink-0" />
          <span>View File</span>
        </a>
      </AppTableCell>
    </AppTableRow>
  );
}
